import React, { FC } from 'react';
import { useTranslations } from '@lang';
import { Button } from '@common/components/Button';
import Markdown from '@common/components/Markdown';
import SectionBanner from '@common/components/SectionBanner';
import { SectionHero } from '@common/components/SectionHero';
import { Routes } from '@common/constants';
import { JourneyParams, Page, Pagination } from 'shared-types/Common';
import { sortEntityByPriority } from '@utils/helpers';
import { Link } from 'shared-routing';
import EntityPreviewRow from './components/EntityPreviewRow';
import {
  FACILITIES_OPTION,
  PREVIEW_ITEMS_QUANTITY,
  SPA_CLASSES_OPTION,
  SPA_DAYS_OPTION,
  TREATMENTS_OPTION,
} from './constants';

import {
  SectionDescription,
  SectionRow,
  SectionTitle,
  SpaSectionContainer,
} from './SpaLandingPage.styles';
import { FancySelectBox } from './FancySelectBox';
import { useLabels } from '@features/Menu/useLabels';
import {
  useGetSpaDaysPage,
  useGetSpaFacilitiesPage,
  useGetSpaTreatmentsPage,
  useGetSpaClassesPage,
  useGetSpaClasses,
  useGetAvailableSpaPackages,
  useGetAllSpaPackages,
  useGetSpaTreatments,
  useGetSpaFacilities,
} from '@features/spa/hooks';
import { Property } from 'shared-types/Property';
import { parseDateToString } from '@utils/helpers';
import { some } from '@utils/some';
import { mergeById } from '@utils/mergeById';
import { Spinner } from '@common/components';

const {
  SPA_DAY_LIST,
  SPA_DAY_DETAIL,
  SPA_TREATMENT_LIST,
  SPA_TREATMENT_DETAIL,
  SPA_CLASSES_LIST,
  FACILITY_LIST,
  FACILITY_DETAIL,
} = Routes;

export type SpaLandingPageProps = {
  property: Property;
  spaPackagesPage: Page;
  spaPage: Page;
  userHasAccessToSpaClasses: boolean;
  spaPackagesPagination: Pagination;
  spaFacilitiesPagination: Pagination;
  isSpaDaysEnabled: boolean;
  isTreatmentEnabled: boolean;
  isFacilitiesEnabled: boolean;
  isClassEnabled: boolean;
  language: string;
  journeyParams: JourneyParams;
};

export const SpaLandingPage: FC<SpaLandingPageProps> = ({
  property,
  spaPage = { title: '', content: '', heroBackgroundImage: { url: '' } },
  userHasAccessToSpaClasses,
  isSpaDaysEnabled = false,
  isTreatmentEnabled = false,
  isFacilitiesEnabled = false,
  isClassEnabled = false,
  language,
  journeyParams,
}) => {
  const { t } = useTranslations();
  const labels = useLabels();

  const { data: spaClasses, isFetching: isLoadingSpaClasses } = useGetSpaClasses(
    property.id,
    parseDateToString(journeyParams.spaDate)
  );

  const { data: spaClassesPage } = useGetSpaClassesPage(property.id, language);

  const { data: spaFacilitiesPage } = useGetSpaFacilitiesPage(property.id, language);

  const { data: spaTreatmentsPage } = useGetSpaTreatmentsPage(property.id, language);

  const { data: spaPackagesPage } = useGetSpaDaysPage(property.id, language);

  const { data: spaPackages = [], isFetching: isLoadingPackages } = useGetAvailableSpaPackages(
    property.id,
    { category: null, date: parseDateToString(journeyParams.spaDate) },
    language
  );

  const {
    data: spaPackagesAll = [],
    total: spaPackagesTotal,
    isFetching: isLoadingSpaPackagesAll,
  } = useGetAllSpaPackages(property.id, language);

  const { spaTreatments, isFetching: isLoadingTreatments } = useGetSpaTreatments(
    property.id,
    language,
    parseDateToString(journeyParams.spaDate),
    null,
    4
  );

  const {
    spaTreatments: spaTreatmentsAll,
    total: treatmentsTotal,
    isFetching: isLoadingTreatmentsAll,
  } = useGetSpaTreatments(property.id, language, null, null, 4);

  const { data: spaFacilities, isFetching: isLoadingFacilities } = useGetSpaFacilities(
    property.id,
    language,
    parseDateToString(journeyParams.spaDate),
    null,
    4
  );

  const {
    data: spaFacilitiesAll,
    total: facilitiesTotal,
    isFetching: isLoadingFacilitiesAll,
  } = useGetSpaFacilities(property.id, language, null, null, 4);

  const mergedSpaPackages = mergeById(spaPackages, spaPackagesAll);
  const mergedSpaTreatments = mergeById(
    spaTreatments.map((t) => ({ ...t, id: Number(t.id) })),
    spaTreatmentsAll.map((t) => ({ ...t, id: Number(t.id) }))
  );
  const mergedSpaFacilities = mergeById(spaFacilities, spaFacilitiesAll);

  const SPA_CATEGORIES_LIST = {
    spaDays: 'spa-days',
    spaTreatments: 'spa-treatments',
    spaClasses: 'spa-classes',
    spaFacilities: 'spa-facilities',
  };
  const showClasses = userHasAccessToSpaClasses && some(spaClasses);
  const SpaModulesPage = {
    [SPA_CATEGORIES_LIST.spaDays]: spaPackagesPage,
    [SPA_CATEGORIES_LIST.spaTreatments]: spaTreatmentsPage,
    [SPA_CATEGORIES_LIST.spaFacilities]: spaFacilitiesPage,
  };
  if (showClasses) {
    SpaModulesPage[SPA_CATEGORIES_LIST.spaClasses] = spaClassesPage;
  }
  const getSpaOptions = () => {
    const spaOptions: { label: string; value: Routes }[] = [];
    if (isSpaDaysEnabled && !!mergedSpaPackages.length) {
      spaOptions.push({
        label: labels?.[SPA_DAYS_OPTION] || t('category.spaDays'),
        value: SPA_DAY_LIST,
      });
    }
    if (isTreatmentEnabled && !!mergedSpaTreatments.length) {
      spaOptions.push({
        label: labels?.[TREATMENTS_OPTION] || t('category.treatments'),
        value: SPA_TREATMENT_LIST,
      });
    }
    if (isClassEnabled && showClasses) {
      spaOptions.push({
        label: labels?.[SPA_CLASSES_OPTION] || t('common.classes'),
        value: SPA_CLASSES_LIST,
      });
    }
    if (isFacilitiesEnabled && !!mergedSpaFacilities.length) {
      spaOptions.push({
        label: labels?.[FACILITIES_OPTION] || t('app.facilities'),
        value: FACILITY_LIST,
      });
    }
    return spaOptions;
  };
  const isLoading =
    isLoadingTreatments ||
    isLoadingFacilities ||
    isLoadingPackages ||
    isLoadingSpaClasses ||
    isLoadingSpaPackagesAll ||
    isLoadingTreatmentsAll ||
    isLoadingFacilitiesAll;

  return (
    <>
      <SectionHero
        backgroundUrl={spaPage?.heroBackgroundImage?.url}
        summary={spaPage?.content}
        title={spaPage?.title}
        imageLabel={t('spa.spaHeroImage')}
        renderButton={<FancySelectBox options={getSpaOptions()} />}
      />

      <SpaSectionContainer>
        <Spinner isActive={isLoading} />
        {!isLoading && (
          <>
            {isSpaDaysEnabled && !!mergedSpaPackages.length && (
              <SectionRow>
                <SectionTitle>
                  {spaPackagesPage ? spaPackagesPage.title : labels?.spadays || t('app.spaDays')}
                </SectionTitle>
                <SectionDescription>
                  {spaPackagesPage && <Markdown text={spaPackagesPage.content} />}
                </SectionDescription>
                <EntityPreviewRow
                  route={SPA_DAY_DETAIL}
                  items={sortEntityByPriority(mergedSpaPackages, 'displayOrder').slice(
                    0,
                    PREVIEW_ITEMS_QUANTITY
                  )}
                />
                <Link route={SPA_DAY_LIST}>
                  <Button>
                    {t('spaLanding.showAllSpaDays', {
                      spaDaysCustomLabel: labels?.spadays || 'Spa Days',
                      allSpaDays: spaPackagesTotal,
                    })}
                  </Button>
                </Link>
              </SectionRow>
            )}

            {isTreatmentEnabled && !!mergedSpaTreatments.length && (
              <SectionRow>
                <SectionTitle>
                  {spaTreatmentsPage
                    ? spaTreatmentsPage.title
                    : t('spaLanding.treatmentsAndTherapies', {
                        treatmentsCustomLabel: labels?.treatments || 'Treatments',
                      })}
                </SectionTitle>
                <SectionDescription>
                  {spaTreatmentsPage && <Markdown text={spaTreatmentsPage.content} />}
                </SectionDescription>

                <EntityPreviewRow
                  route={SPA_TREATMENT_DETAIL}
                  items={sortEntityByPriority(mergedSpaTreatments).slice(0, PREVIEW_ITEMS_QUANTITY)}
                />
                <Link route={SPA_TREATMENT_LIST}>
                  <Button>
                    {t('spaLanding.showAllTreatmentsAndTherapies', {
                      allSpaTreatments: treatmentsTotal,
                      treatmentsCustomLabel: labels?.treatments || 'Treatments',
                    })}
                  </Button>
                </Link>
              </SectionRow>
            )}

            {isFacilitiesEnabled && !!mergedSpaFacilities.length && (
              <SectionRow>
                <SectionTitle>
                  {spaFacilitiesPage?.title ?? (labels?.facilities || t('app.facilities'))}
                </SectionTitle>
                <SectionDescription>
                  {spaFacilitiesPage?.content ? (
                    <Markdown text={spaFacilitiesPage.content} />
                  ) : null}
                </SectionDescription>

                <EntityPreviewRow
                  route={FACILITY_DETAIL}
                  items={sortEntityByPriority(mergedSpaFacilities).slice(0, PREVIEW_ITEMS_QUANTITY)}
                />
                <Link route={FACILITY_LIST}>
                  <Button>
                    {t('spaLanding.showAllFacilities', {
                      allSpaFacilities: facilitiesTotal,
                      facilitiesCustomLabel: labels?.facilities || 'Facilities',
                    })}
                  </Button>
                </Link>
              </SectionRow>
            )}

            {isClassEnabled && showClasses ? (
              <SectionRow>
                <SectionTitle>
                  {spaClassesPage ? spaClassesPage.title : labels?.classes || t('common.classes')}
                </SectionTitle>
                <SectionDescription>
                  {spaClassesPage && <Markdown text={spaClassesPage.content} />}
                </SectionDescription>
                <SectionBanner
                  backgroundImage={spaClassesPage ? spaClassesPage.heroBackgroundImage : null}
                  label={t('spaLanding.showClassTimetable')}
                  route={SPA_CLASSES_LIST}
                />
              </SectionRow>
            ) : null}
          </>
        )}
      </SpaSectionContainer>
    </>
  );
};
